import Banner from "src/components/molecules/banner/Banner";
import ActionPanel from "../action-panel/action-panel";
import ReactQuill from "react-quill";
import { useEffect, useRef, useState } from "react";
import "react-quill/dist/quill.snow.css";
import { useAuth } from "../auth/auth-context";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";
import EmailDisplay from "src/components/atoms/email-display/email-display";
import RadioGroup from "src/components/molecules/radio-button-group/radio-button-groups";
import { SupportTicketViewModel } from "./support-ticket-viewmodel";
import Modal from "src/components/molecules/modal/modal";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import ReactDatePicker from "react-datepicker";
import CheckBox from "src/components/molecules/check-box/check-box";
import CopyToClipboardButton from "src/components/atoms/copy-to-clipboard-button/copy-to-clipboard-button";
import { AccountViewModel } from "../account-selection/account-selection-view-model";

const Support = () => {
	const { user } = useAuth();
	const [description, setDescription] = useState("");
	const [subject, setSubject] = useState("");
	// const [fileSrc, setFileSrc] = useState<IFileInfo | undefined>();
	const [severity, setSeverity] = useState(4);
	const [showSuccess, setShowSuccess] = useState(false);
	const [showFailure, setShowFailure] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [scheduleCall, setScheduleCall] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [meetingTime, setMeetingTime] = useState<number | undefined>();
	const [startDate, setStartDate] = useState<Date | null>(new Date());
	const viewModel = useRef<SupportTicketViewModel | null>(null);
	const [accountId, setAccountId] = useState("");
	const [accountName, setAccountName] = useState("");
	const accountViewModel = useRef<AccountViewModel | null>(null);

	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new SupportTicketViewModel(user.accessToken);
			accountViewModel.current = new AccountViewModel(user.accessToken);

			fetchAccountsAsync();
		}
	}, []);

	useEffect(() => {
		if (severity > 2) {
			setScheduleCall(false);
		}
	}, [severity]);

	const resetForm = async () => {
		setSubject("");
		setDescription("");
		setSeverity(4);
	};

	const fetchAccountsAsync = async () => {
		if (accountViewModel.current) {
			const currentAccount =
				await accountViewModel.current.getAccountAsync();

			console.log(`current account`, currentAccount);
			if (currentAccount) {
				setAccountId(currentAccount.accountId);
				setAccountName(currentAccount.name);
			}
		}
	};

	const handleSubmit = async () => {
		if (viewModel.current) {
			setSubmitting(true);
			const response = await viewModel.current.createSupportTicket({
				accountId: accountId,
				name: accountName,
				title: subject,
				description: description,
				severity: severity,
				scheduleMeeting: scheduleCall,
				meetingStartTime: scheduleCall ? meetingTime : undefined,
			});
			setSubmitting(false);
			if (response) {
				setShowSuccess(true);
				setSuccessMessage(response.message);
				await resetForm();
			} else {
				setShowFailure(true);
			}
		}
	};

	const handleCancel = async () => {
		console.log(`handle cancel`);
	};

	const handlePriorityChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		setSeverity(parseInt(selectedOption.value));
	};

	const getMaxSelectableDate = () => {
		// ✅ Prevent selection beyond 4 hours from now
		return new Date(new Date().getTime() + 4 * 60 * 60 * 1000);
	};

	const getMinSelectableTime = (selectedDate: Date | null) => {
		const now = new Date();

		// ✅ If selected date is today, restrict to current time or later
		if (
			selectedDate &&
			selectedDate.toDateString() === now.toDateString()
		) {
			return now;
		}

		// ✅ Otherwise, allow selecting any time (midnight onwards)
		return new Date(0, 0, 0, 0, 0);
	};

	const getMaxSelectableTime = (selectedDate: Date | null) => {
		const now = new Date();
		const maxAllowedTime = new Date(now.getTime() + 4 * 60 * 60 * 1000); // 4 hours from now

		// ✅ If selected date is today, restrict to maxAllowedTime
		if (
			selectedDate &&
			selectedDate.toDateString() === now.toDateString()
		) {
			return maxAllowedTime;
		}

		// ✅ Otherwise, set max time for other days as 4 hours from now
		return maxAllowedTime > new Date(0, 0, 0, 23, 59)
			? new Date(0, 0, 0, 23, 59)
			: maxAllowedTime;
	};

	return (
		<>
			<Banner label="Create a support ticket">
				<div className="bg-white p-6 rounded-lg drop-shadow-2xl">
					<div className="flex flex-col items-start justify-center gap-4 w-full p-4">
						<div className="flex flex-col items-start justify-center gap-4 w-full">
							<span className="bg-gradient-to-r from-sky-900 to-sky-700 font-Poppins font-semibold text-xl w-full p-2 text-white border-b-4 border-orange-600">
								Contact information
							</span>
							<div className="grid grid-cols-[auto_1fr] gap-2">
								<span className="font-Poppins font-semibold text-md text-gray-700">
									Name
								</span>
								<span className="font-Poppins font-medium text-md text-gray-900">
									{user?.givenName} {user?.familyName}
								</span>
								<span className="font-Poppins font-semibold text-md text-gray-700">
									Email
								</span>
								<span className="font-Poppins font-medium text-md text-gray-900">
									{user && (
										<EmailDisplay
											emailAddress={user.email}
										></EmailDisplay>
									)}
								</span>
								<span className="font-Poppins font-semibold text-md text-gray-700">
									Mobile
								</span>
								<span className="font-Poppins font-medium text-md text-gray-900">
									{user && user.phoneNumber && (
										<PhoneNumberDisplay
											number={user.phoneNumber}
											hasPlus={true}
										></PhoneNumberDisplay>
									)}
								</span>
							</div>
						</div>
					</div>
					<div className="flex flex-col items-center justify-center gap-8 p-4">
						<InputBox
							label={"Subject"}
							type={"text"}
							value={subject}
							onChange={function (value: string): void {
								setSubject(value);
							}}
							maxLength={100}
							required
						></InputBox>
						<div className="w-full mx-auto flex flex-col items-start justify-center gap-2">
							<span className="font-Poppins font-semibold w-full text-left">
								Description: please provide as much information
								as possible, this will help the team to identify
								and fix the issue on priority
							</span>
							<ReactQuill
								theme="snow"
								value={description}
								onChange={setDescription}
								className="react-quill-custom w-full max-w-full overflow-y-auto"
							/>
						</div>

						{/* <div className="flex flex-col items-start justify-center border-2 border-dashed border-gray-300 p-4 w-full">
							<span className="font-Poppins font-semibold text-sm w-full text-left">
								Attachment
							</span>
							<UploadButton
								buttonType="File"
								onUploadSuccess={handleFileChange}
								onClear={() => setFileSrc(undefined)}
							></UploadButton>
						</div> */}

						<div className="w-full flex flex-col items-start justify-start gap-6">
							<div className="flex flex-col items-center justify-center gap-4">
								<span className="font-Poppins font-semibold w-full text-left">
									Severity of the issue
								</span>
								<RadioGroup
									options={[
										{
											label: `Severity 1`,
											value: "1",
										},
										{
											label: `Severity 2`,
											value: "2",
										},
										{
											label: `Severity 3`,
											value: "3",
										},
										{
											label: `Severity 4`,
											value: "4",
										},
									]}
									defaultSelected={`${severity}`}
									onChange={handlePriorityChange}
								/>
							</div>
							{severity <= 2 && (
								<>
									<CheckBox
										title="Schedule a call with support team?"
										onChange={function (
											value: boolean
										): void {
											setScheduleCall(value);
										}}
										checked={scheduleCall}
									></CheckBox>
									{scheduleCall && (
										<div className="flex flex-col items-center justify-center gap-4">
											<span className="font-Poppins font-semibold w-full text-left">
												Meeting time
											</span>
											<ReactDatePicker
												selected={startDate}
												onChange={(date) => {
													if (date) {
														setStartDate(date);
														setMeetingTime(
															Math.floor(
																date.getTime() /
																	1000
															)
														); // ✅ Store Epoch time in seconds
													}
												}}
												showTimeSelect
												dateFormat="MMMM d, yyyy h:mm aa"
												minDate={new Date()} // ✅ Restrict past dates
												maxDate={getMaxSelectableDate()} // ✅ Prevent selecting dates beyond 4 hours from now
												minTime={getMinSelectableTime(
													startDate
												)} // ✅ Prevent past time selection
												maxTime={getMaxSelectableTime(
													startDate
												)} // ✅ Prevent selecting time beyond 4 hours
												className="cursor-pointer w-full font-Poppins text-md border-gray-300 shadow-sm focus:ring focus:ring-blue-200 focus:ring-opacity-50 border-b-2 text-left"
												wrapperClassName="w-[270px]"
												popperClassName="z-[9999]" // Ensure it appears on top
												portalId="root" // Render at the top of the DOM to avoid being hidden
											/>
										</div>
									)}
								</>
							)}
						</div>
						<ActionPanel
							primaryButtonLabel={"Submit"}
							primaryButtonEnabled={
								subject != "" && description != "<p><br></p>"
							}
							secondaryButtonLabel={"Cancel"}
							secondaryButtonEnabled={true}
							primaryButtonAction={function (): void {
								handleSubmit();
							}}
							secondaryButtonAction={function (): void {
								handleCancel();
								resetForm();
							}}
						></ActionPanel>
					</div>
				</div>
				<Modal
					isOpen={showSuccess}
					onClose={function (): void {
						setShowSuccess(false);
					}}
				>
					<span className="font-Poppins font-semibold text-md">
						{successMessage}
					</span>
				</Modal>

				<Modal
					isOpen={showFailure}
					onClose={function (): void {
						setShowFailure(false);
					}}
				>
					<div className="flex flex-col items-center justify-center gap-4">
						<span className="font-Poppins font-medium w-full text-center p-2 text-gray-900">
							Thank you for reaching out!
						</span>
						<div className="flex items-center justify-center gap-1">
							<span className="font-Poppins font-medium w-full text-xl text-center p-2 text-gray-900">
								S-3393939
							</span>
							<CopyToClipboardButton
								text={"S-3393939"}
							></CopyToClipboardButton>
							{/* <button
								className="pr-2 cursor-copy"
								onClick={(e) => {
									e.stopPropagation();
									ClipboardService.copyToClipboard(
										"S-3393939"
									);
								}}
							>
								<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
							</button> */}
						</div>
						<span className="font-Poppins font-medium w-full text-center p-2 text-gray-900">
							We have received your support request, and our team
							is reviewing the details.
						</span>
						<span className="font-Poppins font-medium w-full text-center p-2 text-gray-900">
							A member of our support team will connect with you
							shortly to troubleshoot the issue and assist you
							further. In the meantime, if you have any additional
							information that might help us resolve your request
							faster, please feel free to update the ticket.
						</span>
						<span className="font-Poppins font-medium w-full text-center p-2 text-gray-900">
							We appreciate your patience and will get back to you
							as soon as possible.
						</span>
					</div>
					{/* <div className="flex flex-col items-center justify-center gap-4">
						<ExclamationCircleIcon className="w-24 h-24 text-red-500"></ExclamationCircleIcon>
						<span className="bg-red-500 font-Poppins font-medium w-full text-center p-2 text-white">
							The system is unable to create a support ticket,
							please call on +61 477 830 826
						</span>
					</div> */}
				</Modal>

				<LoadingModal
					open={submitting}
					message="Submitting your ticket"
				></LoadingModal>
			</Banner>
		</>
	);
};

export default Support;
